import { useEffect, useRef, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { getApi } from '../../../api/call.api';
import { urlApi } from '../../../api/url.api';
import { useSocket } from '../../../providers/SocketProvider';
import { socketKeys } from '../../../statics/socketKeys';
import ChatBox from '../Component/ChatComponents/ChatBox';
import ChatSessionSidebar from '../Component/ChatComponents/ChatSessionSidebar';
import ChatUserTile from '../Component/ChatComponents/ChatUserTile';

const ChatSection = () => {
    const { joinAdminInRoom, socket } = useSocket();
    const [selectedUser, setSelectedUser] = useState(null);
    const [userList, setUserList] = useState({ count: 0, items: [] });
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const listRef = useRef();
    const [selectedFilter, setSelectedFilter] = useState(1);
    const [chatSessionCounts, setChatSessionCounts] = useState({})

    const limit = 30;

    const getUserList = async (currentPage = 1) => {
        setLoading(true);
        try {
            const resp = await getApi(`${urlApi.getVisitorList}?limit=${limit}&page=${currentPage}&chatSessionStatus=${selectedFilter}`);
            if (resp?.responseCode === 200) {
                const newItems = resp?.data?.items || [];
                setUserList(prev => ({
                    count: resp?.data?.count,
                    // check if the data is belong to current filter by item[0].status == selectedFilter then append otherwise start fresh
                    items: currentPage === 1 ? newItems : [...prev.items, ...newItems],
                }));

                // ✅ STOP loading more if no new items or fewer than `limit`
                if (newItems.length < limit) {
                    setHasMore(false);
                }
            } else {
                setHasMore(false); // ✅ If API fails, stop further calls
            }
        } catch (error) {
            console.error("Error fetching users:", error);
            setHasMore(false); // ✅ Stop calling on API error
        }
        setLoading(false);
    };

    const getChatSessionCounts = async () => {
        let resp = await getApi(urlApi.getChatSessionCounts);
        if (resp?.responseCode === 200) {
            setChatSessionCounts(resp?.data);
        } else {
            setChatSessionCounts({});
        }
    }

    // Initial fetch
    useEffect(() => {
        getUserList(1);
    }, [selectedFilter]);

    useEffect(() => {
        getChatSessionCounts();
        return () => { }
    }, [])


    // Socket live updates
    useEffect(() => {
        if (!socket) return;

        socket.on(socketKeys.UPDATE_CHAT_LIST, (data) => {
            if (data) {
                getChatSessionCounts();
                updateUserList(data?._id, data);
                setPage(1);
                setHasMore(true);
            }
        });

        socket.on(socketKeys.ON_VISITOR_OFFLINE, (data) => {
            if (data) {
                removeUser(data);
            }
        })

        return () => {
            socket.off(socketKeys.UPDATE_CHAT_LIST);
            socket.off(socketKeys.ON_VISITOR_OFFLINE);
        };
    }, [socket]);

    const updateUserList = (dataId, data) => {
        // setSelectedFilter(data?.status)
        if (!dataId) return;
        if (data?.chatSessionMode == 1) return;
        if (data?.status != selectedFilter) return;

        setUserList(prev => {
            const existingItems = prev.items || [];
            const itemExists = existingItems.some(item => String(item?._id) === dataId);

            if (itemExists) {
                const updatedItems = existingItems.map(item =>
                    String(item?._id) === dataId ? data : item
                );
                return {
                    ...prev,
                    items: updatedItems,
                    count: updatedItems.length,
                };
            } else {
                const updatedItems = [data, ...existingItems];
                return {
                    ...prev,
                    items: updatedItems,
                    count: updatedItems.length,
                };
            }
        });
    };

    const moveChat = (dataId, data) => {
        setUserList(prev => {
            // Safely compare IDs with type conversion
            const existingIndex = prev.items.findIndex(item =>
                String(item._id) === String(dataId)
            );

            // Return unchanged if not found
            if (existingIndex === -1) return prev;

            // Create updated item with merged data
            const updatedItem = {
                ...prev.items[existingIndex],
                lastMessage: data.message,
                updatedAt: data.updatedAt
            };

            // Create new array structure
            const updatedItems = [
                updatedItem,
                ...prev.items.slice(0, existingIndex),
                ...prev.items.slice(existingIndex + 1)
            ];

            return {
                ...prev,
                items: updatedItems
            };
        });
    };

    const removeUser = (data) => {
        setUserList(prev => {
            const filteredItems = prev?.items?.filter(item => item?.visitorId !== data?.visitorId);
            return { ...prev, items: filteredItems };
        });
    };



    // Scroll handler for infinite scroll
    const handleScroll = () => {
        const container = listRef.current;
        if (!container || loading || !hasMore) return;

        const { scrollTop, scrollHeight, clientHeight } = container;
        if (scrollTop + clientHeight >= scrollHeight - 10) {
            setPage(prev => prev + 1);
        }
    };

    // Attach scroll handler to div
    useEffect(() => {
        const container = listRef.current;
        if (container) {
            container.addEventListener('scroll', handleScroll);
            return () => container.removeEventListener('scroll', handleScroll);
        }
    }, [loading, hasMore]);

    // Load more when page changes
    useEffect(() => {
        if (page > 1) {
            getUserList(page);
        }
    }, [page]);

    const onUserTileClick = (userData) => {
        joinAdminInRoom(userData?._id);
        setSelectedUser(userData);
    };

    return (
        <div className="flex h-full">
            {/* User List Side */}
            <div className="bg-white w-[320px]">
                <div className="flex p-2 gap-2 outline outline-1 rounded-md outline-gray-100 justify-around">
                    <button className={`text-xs w-full py-0.5 rounded-md ${selectedFilter == 1 && '!bg-primary text-white'}`} onClick={() => { setSelectedFilter(1); setSelectedUser(null); setUserList([]) }}>Active <span className='text-xs'>({chatSessionCounts?.active || 0})</span></button>
                    <button className={`text-xs w-full py-0.5 rounded-md ${selectedFilter == 2 && '!bg-primary text-white'}`} onClick={() => { setSelectedFilter(2); setSelectedUser(null); setUserList([]) }}>Unread <span className='text-xs'>({chatSessionCounts?.pending || 0})</span></button>
                    <button className={`text-xs w-full py-0.5 rounded-md ${selectedFilter == 3 && '!bg-primary text-white'}`} onClick={() => { setSelectedFilter(3); setSelectedUser(null); setUserList([]) }}>Closed <span className='text-xs'>({chatSessionCounts?.closed || 0})</span></button>
                    <button className={`text-xs w-full py-0.5 rounded-md ${selectedFilter == 5 && '!bg-primary text-white'}`} onClick={() => { setSelectedFilter(5); setSelectedUser(null); setUserList([]) }}>Unresolved <span className='text-xs'>({chatSessionCounts?.unresolved || 0})</span></button>
                </div>

                <div
                    className="max-h-[calc(100vh-130px)] h-full overflow-y-auto overflow-x-hidden py-2"
                    ref={listRef}
                >
                    {userList?.items?.map((item) => (
                        <div key={item._id} onClick={() => onUserTileClick(item)} className="max-w-[320px] w-full">
                            <ChatUserTile userData={item} selectedUser={selectedUser} />
                        </div>
                    ))}

                    {loading && (
                        <div className="flex justify-center items-center py-4 text-xs">
                            <Spinner size="sm" />
                            <span className="ms-2">Loading...</span>
                        </div>
                    )}

                    {!hasMore && (
                        <div className="text-center text-xs text-gray-400 py-3">No more users</div>
                    )}
                </div>
            </div>

            {/* Chat Box Side */}
            {selectedUser &&
                <div className='flex w-full'>
                    <ChatBox setSelectedUser={(data) => setSelectedUser(prev => ({ ...prev, colorCode: data }))} selectedUser={selectedUser} socket={socket} getUserList={() => { getUserList(); getChatSessionCounts() }} messageReceiveFunction={(id, item) => moveChat(id, item)} />
                    <ChatSessionSidebar onUserTileClick={(data) => onUserTileClick(data)} visitorData={selectedUser} />
                </div>
            }
        </div>
    );
};

export default ChatSection;
